import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Beaker, Leaf, ShieldCheck, Sparkles } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { BlogByline } from "@/components/BlogByline";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { buildBlogHead } from "@/lib/blogMeta";
import { RelatedReading } from "@/components/RelatedReading";
import { References } from "@/components/References";
import { ClusterLinks } from "@/components/ClusterLinks";
import { AuthorityFlow } from "@/components/AuthorityFlow";
import { relatedArticles } from "@/lib/clusters";
import { ClinicalProductPitch } from "@/components/ClinicalProductPitch";

const URL = "https://truwe.in/blog/liver-detox-truth";
const TITLE = "Liver Detox: Myths vs Evidence. What Actually Works (2026) | TruWe";

export const Route = createFileRoute("/blog/liver-detox-truth")({
  component: LiverArticle,
  head: () => buildBlogHead({
    slug: "liver-detox-truth",
    title: "Liver Detox: Myths vs Evidence. What Actually Works (2026) | TruWe",
    description: "Evidence-based guide to liver detox, what your liver does, why juice cleanses don't work, and the ingredients (silymarin, NAC, ALA, choline) with actual clinical data.",
    ogTitle: "Liver Detox: Myths vs Evidence",
    ogDescription: "Forget the juice cleanse. Here's how the liver actually detoxes, what fatty liver looks like, and the four ingredients with real clinical data.",
    headline: "Liver Detox: Myths vs Evidence. What Actually Works",
    breadcrumbName: "Liver Detox: Myths vs Evidence. What Actually Works",
    datePublished: "2026-06-14",
    dateModified: "2026-07-25",
    faqs: [
              {
                q: "Do juice cleanses actually detox the liver?",
                a: "No. The liver runs continuous two-phase enzymatic detox 24/7 and cannot be 'flushed' by juice. Juice cleanses spike fructose, which the liver converts to fat, the opposite of helpful for fatty liver, the most common modern liver problem.",
              },
              {
                q: "What are the signs of a sluggish liver?",
                a: "Persistent fatigue (especially morning), brain fog, stubborn upper-abdominal weight, dull skin, sensitivity to alcohol or fatty meals, dark circles and raised liver enzymes (ALT, AST, GGT) on a blood test.",
              },
              {
                q: "Which supplements have real evidence for liver health?",
                a: "Silymarin (milk thistle), N-acetylcysteine (NAC), alpha-lipoic acid (ALA), choline and a multi-strain probiotic. Each has multiple human RCTs in NAFLD/MAFLD and elevated liver enzymes.",
              },
              {
                q: "How long does it take to improve liver enzymes?",
                a: "Lifestyle plus targeted supplementation typically drops ALT/AST by 20-40% in 12 weeks. NAFLD can fully reverse in 6-12 months with consistent intervention.",
              },
            ],
  }),
});

function LiverArticle() {
  return (
    <article className="bg-background pb-24 pt-28 md:pt-36">
      <div className="mx-auto max-w-3xl px-4 md:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">
            Clinical Library · Liver & Gut
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight text-brand-deep md:text-5xl">
            Liver Detox: Myths vs Evidence. What Actually Works
          </h1>
          <BlogByline date="2026-06-14" readingTime="10 min read" />
          <div className="mt-4"><ReadWithLlm title={TITLE} url={URL} kind="article" /></div>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            One in three Indian adults now has non-alcoholic fatty liver disease (NAFLD), and
            most don't know it until a routine ultrasound flags it. The wellness internet's
            answer is a 7-day juice cleanse. That's not how the liver works. Here is what it
            actually does, what's going wrong, and the short list of ingredients that the
            evidence backs.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            How the liver actually detoxes
          </h2>
          <p className="mt-3 text-base text-foreground/85">
            The liver runs detox in two enzymatic phases, every minute, every day. You don't
            kick-start it. You either give it the cofactors it needs, or you don't.
          </p>
          <ul className="mt-4 space-y-3 text-base text-foreground/85">
            <li>
              <strong>Phase I, cytochrome P450 enzymes</strong> convert fat-soluble toxins
              (alcohol, medications, pesticides, hormones) into reactive intermediates. This
              phase generates oxidative stress and needs B-vitamins and antioxidants to run
              cleanly.
            </li>
            <li>
              <strong>Phase II, conjugation</strong> attaches a water-soluble molecule (glycine,
              sulfate, glutathione, glucuronic acid) to the intermediate so it can be excreted in
              urine or bile. This is where glutathione, NAC, choline and B12 matter.
            </li>
            <li>
              <strong>Phase III, elimination</strong> ships the conjugated toxin out via bile
              into the gut, where it must be carried out in stool. A constipated or dysbiotic gut
              re-absorbs toxins back into the liver, which is why probiotics belong in a liver
              protocol.
            </li>
          </ul>
          <p className="mt-4 text-base text-foreground/85">
            Notice what's missing: there's no "flush" step. The system is enzymatic and continuous.
            Drinking lemon water doesn't speed it up.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            The real problem in 2026: fatty liver
          </h2>
          <p className="mt-3 text-base text-foreground/85">
            Modern liver disease isn't a toxin problem, it's a fuel problem. Excess fructose,
            refined carbs and seed oils get converted into liver fat (de novo lipogenesis). That
            fat triggers insulin resistance, inflammation and eventually scarring (NASH, then
            cirrhosis).
          </p>
          <p className="mt-3 text-base text-foreground/85">
            The early warning signs are subtle: morning sluggishness, mid-section weight that
            won't budge, sensitivity to alcohol, dull skin, a slight ache under the right ribcage,
            slightly raised ALT/GGT on a blood test. By the time it's "fatty liver" on an
            ultrasound, the process has been running for years.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            Myths to retire
          </h2>
          <div className="mt-5 space-y-4">
            {[
              {
                name: "Juice cleanses",
                use: "Counter-productive for fatty liver",
                note: "Fructose is metabolised only by the liver and converted directly to fat. A 'cleanse' of fruit juice is a fat-deposition protocol.",
              },
              {
                name: "Apple cider vinegar shots",
                use: "Tiny benefit, oversold",
                note: "May slightly lower post-meal glucose. Does nothing structural for the liver. Don't drink it neat, destroys enamel.",
              },
              {
                name: "Coffee enemas",
                use: "Avoid",
                note: "No clinical evidence. Real risk of perforation and electrolyte imbalance.",
              },
              {
                name: "Liver flush teas",
                use: "Mostly senna laxatives",
                note: "You're losing water, not toxins. Chronic use damages the colonic nervous system.",
              },
              {
                name: "Detox foot patches",
                use: "Theatrical only",
                note: "The dark sludge is a colour change reaction with water vapour from sweat. Tested in lab, zero excreted toxins.",
              },
            ].map((row) => (
              <div key={row.name} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <Beaker className="h-4 w-4 text-brand" />
                  <h3 className="font-display text-lg font-semibold text-brand-deep">{row.name}</h3>
                </div>
                <p className="mt-1 text-sm font-medium text-brand">{row.use}</p>
                <p className="mt-2 text-sm text-foreground/80">{row.note}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            What does work: the evidence-based stack
          </h2>
          <div className="mt-5 space-y-4">
            {[
              {
                name: "Silymarin (milk thistle)",
                use: "Hepatocyte protection, antioxidant",
                note: "Liposomal silymarin shows 4-10× the absorption of standard milk thistle. Lowers ALT and AST in NAFLD trials, used in Europe as a hepatoprotective adjunct.",
              },
              {
                name: "N-acetylcysteine (NAC)",
                use: "Glutathione precursor",
                note: "Glutathione is the liver's master detox antioxidant. NAC reliably raises hepatic glutathione, used medically for paracetamol overdose.",
              },
              {
                name: "Alpha-lipoic acid (ALA)",
                use: "Mitochondrial antioxidant, insulin sensitiser",
                note: "Liposomal R-ALA recycles vitamins C and E, lowers fasting insulin and ALT in fatty liver studies.",
              },
              {
                name: "Choline",
                use: "Required for fat export from the liver",
                note: "Without choline, the liver cannot package and ship out triglycerides, they pile up. Most Indian diets are choline-deficient.",
              },
              {
                name: "Multi-strain probiotics",
                use: "Gut-liver axis",
                note: "An inflamed gut feeds bacterial endotoxin (LPS) into the liver via the portal vein. Probiotics measurably lower portal LPS in NAFLD trials.",
              },
            ].map((row) => (
              <div key={row.name} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <Beaker className="h-4 w-4 text-brand" />
                  <h3 className="font-display text-lg font-semibold text-brand-deep">{row.name}</h3>
                </div>
                <p className="mt-1 text-sm font-medium text-brand">{row.use}</p>
                <p className="mt-2 text-sm text-foreground/80">{row.note}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            What lifestyle moves the needle most
          </h2>
          <ul className="mt-4 space-y-3 text-base text-foreground/85">
            <li>
              <strong>Cut liquid fructose.</strong> No juices, no sweetened lattes, no soft
              drinks. Whole fruit is fine, the fibre slows fructose absorption.
            </li>
            <li>
              <strong>Lift heavy 2-3×/week.</strong> Muscle is a glucose sink. More muscle = less
              hepatic fat. This out-performs cardio for NAFLD reversal.
            </li>
            <li>
              <strong>12-hour overnight fast.</strong> Gives the liver time to drain its glycogen
              and start burning its own fat stores.
            </li>
            <li>
              <strong>Coffee, 2-3 cups/day.</strong> Genuinely hepatoprotective, lower NAFLD,
              fibrosis and liver cancer risk in cohort studies.
            </li>
            <li>
              <strong>Alcohol &lt; 7 drinks/week.</strong> Even moderate drinking stalls hepatic
              fat clearance.
            </li>
          </ul>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-14 rounded-3xl border border-brand/15 bg-gradient-to-br from-brand/5 via-background to-brand-deep/5 p-8 md:p-10">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand">
              <Sparkles className="h-4 w-4" /> The TruWe formulation
            </div>
            <h2 className="mt-3 font-display text-2xl font-bold text-brand-deep md:text-3xl">
              Liver Biome Plus, the evidence stack in one capsule
            </h2>
            <p className="mt-3 text-base text-foreground/85">
              Liver Biome Plus combines <strong>liposomal silymarin, NAC, liposomal alpha-lipoic
              acid, liposomal curcumin and a multi-strain probiotic</strong> every core ingredient
              in the protocol above, at research-backed doses, in their best-absorbed forms. No senna,
              no fillers, no "detox tea" theatre.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {[
                { icon: Leaf, label: "Liposomal delivery" },
                { icon: ShieldCheck, label: "Third-party tested" },
                { icon: Beaker, label: "USFDA / GMP / FSSAI facility" },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-2 rounded-xl bg-background/70 px-3 py-3 text-sm text-foreground/85 ring-1 ring-border"
                >
                  <Icon className="h-4 w-4 text-brand" />
                  {label}
                </div>
              ))}
            </div>
            <Link
              to="/products/$handle"
              params={{ handle: "liver-biome-plus" }}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-luxury transition hover:bg-brand-deep"
            >
              Explore Liver Biome Plus <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">FAQs</h2>
          <div className="mt-4 space-y-5 text-base text-foreground/85">
            <div>
              <h3 className="font-semibold text-brand-deep">How will I know it's working?</h3>
              <p className="mt-1">
                Morning energy, lighter digestion after fatty meals, and at 12 weeks a repeat
                LFT panel showing ALT, AST and GGT trending down.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-brand-deep">Do I need to "cleanse" first?</h3>
              <p className="mt-1">
                No. Skip the cleanse. Cut fructose and seed oils, train, and run the ingredient
                stack for 90 days. That outperforms any 7-day reset.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-brand-deep">Can I take a liver supplement long term?</h3>
              <p className="mt-1">
                Yes, silymarin, NAC and probiotics have decades of safety data. Re-test LFTs
                annually and adjust with your doctor.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-brand-deep">What if I drink socially?</h3>
              <p className="mt-1">
                NAC + silymarin pre- and post-drinking is the most-studied combination for reducing
                acetaldehyde damage. Best move is still to drink less.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <ClinicalProductPitch cluster="liver-gut" />
          <ClusterLinks cluster="liver-gut" />
          <AuthorityFlow cluster="liver-gut" />
          <References slug="liver-detox-truth" />
          <RelatedReading articles={relatedArticles("liver-detox-truth")} />
        </Reveal>

        <Reveal delay={0.05}>
          <p className="mt-12 text-xs text-muted-foreground">
            This article is for general education only and is not a substitute for medical advice.
            Consult a qualified clinician before starting any supplement, especially if you take
            medication or have an existing liver condition.
          </p>
        </Reveal>
      </div>
    </article>
  );
}
